// Authentication utility functions for secure token handling
import { STORAGE_KEYS } from "./storage";

/**
 * Get the current authentication data from sessionStorage
 * @returns {Object|null} Authentication data or null if not found
 */
export const getAuthData = () => {
  try {
    const authData = sessionStorage.getItem("ecwid_auth");
    return authData ? JSON.parse(authData) : null;
  } catch (error) {
    console.error("Failed to parse auth data:", error);
    return null;
  }
};

/**
 * Get the store ID from localStorage (non-sensitive)
 * @returns {string|null} Store hash ID or null if not found
 */
export const getStoreId = () => {
  return sessionStorage.getItem(STORAGE_KEYS.STORE_ID);
};

/**
 * Get the access token from sessionStorage
 * @returns {string|null} Access token or null if not found
 */
export const getAccessToken = () => {
  const authData = getAuthData();
  return authData?.access_token || null;
};

/**
 * Get the store ID from sessionStorage
 * @returns {string|null} Store ID or null if not found
 */
export const getStoreIdFromAuth = () => {
  const authData = getAuthData();
  return authData?.store_id || null;
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
  const authData = getAuthData();
  const storeId = getStoreId();
  return !!(authData?.access_token && storeId);
};

/**
 * Clear all authentication data
 */
export const clearAuth = () => {
  sessionStorage.removeItem("ecwid_auth");
  sessionStorage.removeItem(STORAGE_KEYS.STORE_ID);
  sessionStorage.removeItem(STORAGE_KEYS.API_KEY);
  sessionStorage.removeItem(STORAGE_KEYS.SETTINGS);
};

/**
 * Make an authenticated API request to Ecwid
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise} Fetch response
 */
export const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  const authData = getAuthData();
  if (!authData?.store_id || !authData?.access_token) {
    throw new Error("Not authenticated");
  }

  const url = `https://app.ecwid.com/api/v3/${authData.store_id}${endpoint}`;
  const headers = {
    'Authorization': `Bearer ${authData.access_token}`,
    'Content-Type': 'application/json',
    ...options.headers
  };

  return fetch(url, {
    ...options,
    headers
  });
}; 